/**
 * Roles editor — the checkbox set plus its Save button, moved out of the old detail drawer.
 *
 * The edit is staged rather than applied per-checkbox: roles go to the server as a complete set
 * (PUT /admin/users/{id}/roles replaces them), so ticking "Moderator" on its own would have to
 * send "User + Moderator" anyway. Staging makes that obvious and gives one undo — closing the
 * screen — instead of three round trips.
 */

import { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material'
import { Shield } from '@mui/icons-material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi } from '@api/endpoints/admin'
import { ADMIN_USERS_KEY, adminUserKey } from './userQueryKeys'
import { AVAILABLE_ROLES, errorMessage } from './userFormatting'

export interface UserRolesEditorProps {
  userId: string
  /** The roles the server currently holds for this user. */
  roles: string[]
}

/**
 * The staged edit is plain `useState` seeded from the prop, so the caller re-seeds it by giving
 * this component a `key` derived from the saved roles (see AdminUserDetailPage). That is
 * deliberately not a syncing effect: an effect would also have to decide what to do with an edit
 * that is half-made when an unrelated refetch lands, and remounting has one obvious answer.
 */
export function UserRolesEditor({ userId, roles }: UserRolesEditorProps) {
  const queryClient = useQueryClient()
  const [editedRoles, setEditedRoles] = useState<string[]>(roles)
  const rolesKey = [...roles].sort().join(',')

  const saveRoles = useMutation({
    mutationFn: () => adminApi.updateUserRoles(userId, { roles: editedRoles }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUserKey(userId) })
      queryClient.invalidateQueries({ queryKey: ADMIN_USERS_KEY })
    },
  })

  const handleRoleChange = (role: string) => {
    setEditedRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]))
  }

  const rolesChanged = [...editedRoles].sort().join(',') !== rolesKey

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Shield fontSize="small" /> Roles
      </Typography>

      {saveRoles.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage(saveRoles.error, 'Failed to update user roles')}
        </Alert>
      )}

      <FormGroup>
        {AVAILABLE_ROLES.map((role) => (
          <FormControlLabel
            key={role}
            control={
              <Checkbox
                checked={editedRoles.includes(role)}
                onChange={() => handleRoleChange(role)}
                disabled={saveRoles.isPending}
              />
            }
            label={role}
          />
        ))}
      </FormGroup>

      {/* Only offered when there is something to save, so the button's presence is the "unsaved
          changes" indicator. */}
      {rolesChanged && (
        <Button
          variant="contained"
          size="small"
          onClick={() => saveRoles.mutate()}
          disabled={saveRoles.isPending}
          sx={{ mt: 1 }}
        >
          {saveRoles.isPending ? 'Saving...' : 'Save Roles'}
        </Button>
      )}
    </Box>
  )
}
